/**
 * Tour step definitions for react-joyride.
 * Each tour targets elements via data-tour attributes.
 */

export const tours = {
  welcome: {
    label: 'Welcome Tour',
    steps: [
      {
        target: '[data-tour="global-nav"]',
        title: 'Sidebar Navigation',
        content: 'Use the sidebar to navigate between sections: Projects, Analytics, Requirements, CAD, Compliance, Verification, Documents, and Knowledge Base.',
        placement: 'right',
        disableBeacon: true,
      },
      {
        target: '[data-tour="project-switcher"]',
        title: 'Project & Branch Switcher',
        content: 'Quickly switch between projects and branches from the header. Your recent projects appear first for quick access.',
        placement: 'bottom',
        disableBeacon: true,
      },
      {
        target: '[data-tour="chat-container"]',
        title: 'e-pilot AI Assistant',
        content: 'Ask e-pilot questions about your engineering data. Toggle CAD mode to generate and modify drawings with AI.',
        placement: 'left',
        disableBeacon: true,
      },
      {
        target: '[data-tour="user-menu"]',
        title: 'Your Account',
        content: 'Access your profile settings, preferences, and sign out from here.',
        placement: 'bottom',
        disableBeacon: true,
      },
    ],
  },

  cad: {
    label: 'Design / CAD Tour',
    steps: [
      {
        target: '[data-tour="cad-file-list"]',
        title: 'File List',
        content: 'Browse all CAD files in your project. Click a file to view details, or use the action buttons for quick operations.',
        placement: 'right',
      },
      {
        target: '[data-tour="cad-import-btn"]',
        title: 'Import CAD Files',
        content: 'Upload CAD files (.dwg, .dxf, .step, .prt, .asm) to your project. Files are versioned automatically.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="cad-viewer"]',
        title: 'CAD Viewer',
        content: 'View 2D drawings and 3D models directly in the browser using the Autodesk Forge Viewer.',
        placement: 'left',
      },
      {
        target: '[data-tour="cad-drawing-selector"]',
        title: 'Drawing Selector',
        content: 'Switch between imported and AI-generated drawings. Status chips show if a drawing is ready, translating, or has an error.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="cad-download"]',
        title: 'Download',
        content: 'Download the source file (STEP, DWG, DXF) of the currently selected drawing.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="cad-measure"]',
        title: 'Measurement Tool',
        content: 'Measure distances, angles, and areas directly on the 2D or 3D model.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="cad-markup"]',
        title: 'Markup & Annotations',
        content: 'Add redline markups, text, arrows, and shapes on top of the drawing for review comments.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="cad-section"]',
        title: 'Section Plane',
        content: 'Cut through 3D models with a section plane to inspect internal geometry.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="cad-fullscreen"]',
        title: 'Fullscreen Mode',
        content: 'Expand the viewer to fill the screen. The e-pilot chat panel stays visible on the right for AI-assisted workflows.',
        placement: 'bottom',
      },
    ],
  },

  requirements: {
    label: 'Requirements Tour',
    steps: [
      {
        target: '[data-tour="req-table"]',
        title: 'Requirements Table',
        content: 'Track all project requirements with status, priority, verification method, and linked parts.',
        placement: 'top',
      },
      {
        target: '[data-tour="req-create"]',
        title: 'Create Requirement',
        content: 'Define new requirements with description, acceptance criteria, and verification approach (test, analysis, inspection, demonstration).',
        placement: 'bottom',
      },
    ],
  },

  documents: {
    label: 'Documents Tour',
    steps: [
      {
        target: '[data-tour="doc-list"]',
        title: 'Document List',
        content: 'Manage project documents organized by category. Upload new documents or browse existing ones.',
        placement: 'top',
      },
      {
        target: '[data-tour="doc-upload"]',
        title: 'Upload Documents',
        content: 'Upload files with metadata. Documents are version-controlled and linked to your project branches.',
        placement: 'bottom',
      },
    ],
  },

  chat: {
    label: 'e-pilot Chat Tour',
    steps: [
      {
        target: '[data-tour="chat-container"]',
        title: 'e-pilot AI Assistant',
        content: 'Type questions or instructions. e-pilot can analyze files, generate CAD drawings, and help with engineering tasks.',
        placement: 'left',
      },
      {
        target: '[data-tour="chat-cad-toggle"]',
        title: 'CAD Mode Toggle',
        content: 'Enable CAD mode to interact with the CAD Agent. In this mode, e-pilot can create and modify AutoCAD drawings and 3D models.',
        placement: 'left',
      },
      {
        target: '[data-tour="chat-input"]',
        title: 'Message Input',
        content: 'Type your message here. You can also attach files for context by clicking the attachment button.',
        placement: 'top',
      },
    ],
  },

  orchestrator: {
    label: 'Pipeline Builder Tour',
    steps: [
      {
        target: '[data-tour="lifecycle-dock"]',
        title: 'Lifecycle Stages',
        content: 'Drag a lifecycle stage (Requirements, CAD, Compliance, etc.) onto the canvas to add a step to your pipeline.',
        placement: 'top',
      },
      {
        target: '[data-tour="orchestrator-canvas"]',
        title: 'Pipeline Canvas',
        content: 'Build visual pipelines by chaining lifecycle stages. Each node lets you pick an e-pilot tool and enter a prompt.',
        placement: 'left',
      },
      {
        target: '[data-tour="orchestrator-generate"]',
        title: 'Run Pipeline',
        content: 'Click Run Pipeline to execute each step. You approve or reject results before the next step runs.',
        placement: 'bottom',
      },
    ],
  },
  compliance: {
    label: 'Compliance Tour',
    steps: [
      {
        target: '[data-tour="compliance-tabs"]',
        title: 'Compliance Tabs',
        content: 'Compliance has two tabs: Standards for managing compliance documents with file attachments, and Dashboard for viewing compliance check results across all drawings.',
        placement: 'bottom',
        disableBeacon: true,
      },
      {
        target: '[data-tour="compliance-add-standard"]',
        title: 'Add Compliance Standard',
        content: 'Click here to add a new compliance standard. Attach a PDF or document, set the verification method and status.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="compliance-standards-table"]',
        title: 'Standards Table',
        content: 'View all compliance standards with their status, verification method, and attached documents. Edit or delete standards using the action buttons.',
        placement: 'top',
      },
      {
        target: '[data-tour="compliance-dashboard-tab"]',
        title: 'Compliance Dashboard',
        content: 'Switch to the Dashboard tab to see aggregated compliance results: how many drawings have been checked, passed, failed, or have warnings.',
        placement: 'bottom',
      },
    ],
  },

  analytics: {
    label: 'Project Analytics Tour',
    steps: [
      {
        target: '[data-tour="analytics-tabs"]',
        title: 'Analytics Views',
        content: 'Analytics has four views: Project Brief for KPI overview, Progression for time-series charts, Risks & Attention for flagged items, and AI Health Summary.',
        placement: 'bottom',
        disableBeacon: true,
      },
      {
        target: '[data-tour="analytics-kpi-cards"]',
        title: 'KPI Dashboard',
        content: 'At-a-glance project metrics: requirements, documents, CAD drawings, and commits. Each card shows a status breakdown.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="analytics-milestone-progress"]',
        title: 'Milestone Progress',
        content: 'Track milestone completion with progress bars. Overdue milestones are highlighted in red.',
        placement: 'top',
      },
    ],
  },

  'knowledge-base': {
    label: 'Knowledge Base Tour',
    steps: [
      {
        target: '[data-tour="kb-header"]',
        title: 'Project Knowledge Base',
        content: 'Pin documents, requirements, and compliance standards here. e-pilot automatically searches these when answering your questions.',
        placement: 'bottom',
        disableBeacon: true,
      },
      {
        target: '[data-tour="kb-add-btn"]',
        title: 'Add Documents',
        content: 'Click to open the picker and select documents, requirements, or standards to add. Pinned items are automatically available as context for e-pilot.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="kb-doc-list"]',
        title: 'Pinned Documents',
        content: 'Your pinned documents appear here with source type badges and indexing status. Remove documents by clicking the delete icon.',
        placement: 'top',
      },
    ],
  },
};

/** Get tour steps by ID */
export const getTourSteps = (tourId) => tours[tourId]?.steps || [];

/** Get all tour IDs */
export const getTourIds = () => Object.keys(tours);
